import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Scale, Shield, Gavel, FileText, Users, Eye, DollarSign, Check, AlertTriangle, Clock, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const whatWeFund = [
  { text: 'Legal challenges against illegal or unethical development on protected land', icon: Gavel },
  { text: 'Lawsuits against municipalities violating environmental regulations', icon: Scale },
  { text: 'Injunctions to stop destruction of wetlands, conservation areas, and wildlife corridors', icon: Shield },
  { text: 'Legal representation for communities facing eminent domain abuse', icon: Users },
  { text: 'Public records litigation forcing government transparency', icon: Eye },
  { text: 'Constitutional challenges protecting property rights of Florida families', icon: FileText },
];

const fundRules = [
  'No political candidate contributions — ever',
  'No administrative salaries paid from this fund',
  'All expenditures published publicly within 48 hours',
  'Annual independent audit published on this page',
  'Members may request full fund accounting at any time by emailing inkmediafc@gmail.com',
];

const sampleCases = [
  { id: 1, name: 'Treasure Coast Corridor Preservation', county: 'St. Lucie County', issue: 'Wetland Development', status: 'ACTIVE', amount: 0, filed: '2026-03-15' },
  { id: 2, name: 'Indian River Estuary Protection', county: 'Indian River County', issue: 'Environmental Violation', status: 'PENDING', amount: 0, filed: '2026-04-01' },
  { id: 3, name: 'Port St. Lucie Greenway Defense', county: 'St. Lucie County', issue: 'Eminent Domain', status: 'ACTIVE', amount: 0, filed: '2026-02-20' },
  { id: 4, name: 'Martin County Conservation Zone', county: 'Martin County', issue: 'Illegal Development', status: 'WON', amount: 0, filed: '2025-12-10' },
];

export default function LegalDefenseFund() {
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/analytics`, { withCredentials: true })
      .then(r => setAnalytics(r.data))
      .catch(() => {});
  }, []);

  const totalContributions = analytics?.membership_revenue || 0;

  return (
    <div className="pt-16" data-testid="legal-defense-page">
      {/* Header */}
      <section className="bg-navy py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-gold font-body mb-3">Legal Defense Fund</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-4 leading-tight">
              Safe Florida Legal Defense Fund — Every Dollar Fights for Florida
            </h1>
            <p className="text-lg text-white/90 font-body italic">
              Full transparency. Real accountability. Your money goes to the fight.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 1 — WHAT IS THE LEGAL DEFENSE FUND */}
      <section className="py-20 bg-white" data-testid="ldf-what-is">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="border-l-4 border-gold pl-6">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy mb-6">What Is the Legal Defense Fund</h2>
            <p className="text-base text-gray-700 font-body leading-relaxed mb-4">
              The Safe Florida Legal Defense Fund is a dedicated pool of member contributions used exclusively to fund legal action protecting Florida communities, land, wildlife, and waters.
            </p>
            <p className="text-base text-gray-700 font-body leading-relaxed">
              Every dollar contributed to the <strong className="text-navy">$10/month Supporter tier</strong> goes directly into this fund. No administrative fees. No overhead. <strong className="text-gold">100% to legal action.</strong>
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 mt-10">
            {[
              { label: '100%', desc: 'To Legal Action', sub: 'Zero overhead' },
              { label: '$0', desc: 'Admin Fees', sub: 'From this fund' },
              { label: '48hr', desc: 'Transparency', sub: 'All decisions published' },
            ].map(({ label, desc, sub }) => (
              <div key={label} className="text-center border border-gray-200 rounded-lg p-6">
                <p className="text-3xl font-heading font-bold text-navy">{label}</p>
                <p className="text-sm font-body font-bold text-gold">{desc}</p>
                <p className="text-xs font-body text-gray-500 mt-1">{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 2 — WHAT WE FUND */}
      <section className="py-20 bg-[#FAFAFA]" data-testid="ldf-what-we-fund">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <Gavel className="w-10 h-10 text-navy mx-auto mb-3" />
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy mb-3">What We Fund</h2>
            <p className="text-base text-gray-600 font-body">Every case we take on protects Florida families and communities.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {whatWeFund.map(({ text, icon: Icon }, i) => (
              <div key={i} className="flex items-start gap-4 bg-white border border-gray-200 rounded-lg p-5 card-hover" data-testid={`fund-item-${i}`}>
                <div className="w-10 h-10 bg-navy rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-gold" />
                </div>
                <p className="text-sm text-gray-700 font-body leading-relaxed pt-1">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 — FUND TRANSPARENCY DASHBOARD */}
      <section className="py-20 bg-white" data-testid="ldf-transparency-dashboard">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <DollarSign className="w-10 h-10 text-gold mx-auto mb-3" />
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy mb-3">Fund Transparency Dashboard</h2>
            <p className="text-base text-gray-600 font-body">Live tracking of every dollar and every case.</p>
          </div>

          {/* Fund Tracker Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
            {[
              { label: 'Contributions This Month', value: `$${totalContributions.toLocaleString()}`, color: 'text-navy' },
              { label: 'Cases Funded to Date', value: sampleCases.length, color: 'text-navy' },
              { label: 'Active Cases', value: sampleCases.filter(c => c.status === 'ACTIVE').length, color: 'text-gold' },
              { label: 'Cases Won', value: sampleCases.filter(c => c.status === 'WON').length, color: 'text-forest' },
              { label: 'Cases Pending', value: sampleCases.filter(c => c.status === 'PENDING').length, color: 'text-sky-custom' },
              { label: 'Dollars Deployed', value: `$${totalContributions.toLocaleString()}`, color: 'text-navy' },
            ].map(({ label, value, color }) => (
              <div key={label} className="border border-gray-200 rounded-lg p-4 text-center">
                <p className={`text-2xl font-heading font-bold ${color}`}>{value}</p>
                <p className="text-xs font-body text-gray-500 mt-1">{label}</p>
              </div>
            ))}
          </div>

          {/* Case Table */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-navy px-5 py-3">
              <h3 className="text-white font-body font-bold text-sm">Active & Recent Cases</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-body">
                <thead>
                  <tr className="bg-[#FAFAFA] border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-gray-500 font-medium">Case</th>
                    <th className="text-left py-3 px-4 text-gray-500 font-medium">County</th>
                    <th className="text-left py-3 px-4 text-gray-500 font-medium">Issue Type</th>
                    <th className="text-left py-3 px-4 text-gray-500 font-medium">Status</th>
                    <th className="text-left py-3 px-4 text-gray-500 font-medium">Filed</th>
                  </tr>
                </thead>
                <tbody>
                  {sampleCases.map(c => (
                    <tr key={c.id} className="border-b border-gray-100" data-testid={`case-row-${c.id}`}>
                      <td className="py-3 px-4 font-medium text-navy">{c.name}</td>
                      <td className="py-3 px-4 text-gray-600">{c.county}</td>
                      <td className="py-3 px-4"><Badge className="bg-navy/10 text-navy text-xs">{c.issue}</Badge></td>
                      <td className="py-3 px-4">
                        <Badge className={`text-xs ${c.status === 'ACTIVE' ? 'bg-gold/20 text-gold' : c.status === 'WON' ? 'bg-forest/20 text-forest' : 'bg-sky-custom/20 text-navy'}`}>
                          {c.status}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-gray-500">{c.filed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4 — HOW FUNDS ARE ALLOCATED */}
      <section className="py-20 bg-navy" data-testid="ldf-allocation">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-8">
            <Users className="w-10 h-10 text-gold mx-auto mb-3" />
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-white mb-6">How Funds Are Allocated</h2>
          </div>
          <div className="bg-white/10 border border-white/20 rounded-lg p-8">
            <p className="text-base text-white/90 font-body leading-relaxed mb-6">
              <strong className="text-gold">Founding Members ($250/month)</strong> and <strong className="text-gold">Seat at the Table members</strong> vote on which cases receive Legal Defense Fund support.
            </p>
            <p className="text-base text-white/90 font-body leading-relaxed mb-6">
              Every funding decision is published publicly on this page within <strong className="text-gold">48 hours</strong> of the vote. No case is funded without member approval.
            </p>
            <div className="grid sm:grid-cols-3 gap-4 mt-6">
              {[
                { icon: Users, label: 'Member Vote', desc: 'Every case requires member approval' },
                { icon: Clock, label: '48 Hour Publish', desc: 'All decisions published within 48 hours' },
                { icon: Eye, label: 'Full Transparency', desc: 'Public record of every allocation' },
              ].map(({ icon: Icon, label, desc }) => (
                <div key={label} className="bg-white/10 border border-white/20 rounded-lg p-4 text-center">
                  <Icon className="w-6 h-6 text-gold mx-auto mb-2" />
                  <p className="font-body font-bold text-white text-sm">{label}</p>
                  <p className="text-xs text-white/70 font-body mt-1">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5 — FUND RULES */}
      <section className="py-20 bg-white" data-testid="ldf-rules">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <BookOpen className="w-10 h-10 text-navy mx-auto mb-3" />
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy mb-3">Fund Rules</h2>
            <p className="text-base text-gray-600 font-body">Non-negotiable rules that govern every dollar in the Legal Defense Fund.</p>
          </div>
          <div className="space-y-4">
            {fundRules.map((rule, i) => (
              <div key={i} className="flex items-start gap-4 border border-gray-200 rounded-lg p-5" data-testid={`fund-rule-${i}`}>
                <div className="w-8 h-8 bg-forest rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-white" />
                </div>
                <p className="text-base text-gray-700 font-body leading-relaxed pt-0.5">{rule}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 p-4 bg-gold/10 border border-gold/30 rounded-lg">
            <p className="text-sm font-body text-navy">
              <strong>Contact for fund accounting:</strong>{' '}
              <a href="mailto:inkmediafc@gmail.com" className="text-gold underline hover:text-gold/80">inkmediafc@gmail.com</a>
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-forest">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <Scale className="w-12 h-12 text-gold mx-auto mb-4" />
          <h2 className="text-3xl font-heading font-bold text-white mb-4">Contribute to the Legal Defense Fund</h2>
          <p className="text-lg text-white/90 font-body mb-8">Join as a Supporter for $10/month. Every dollar goes directly to protecting Florida.</p>
          <Link to="/programs/membership" data-testid="ldf-cta-join">
            <Button size="lg" className="bg-gold text-navy hover:bg-gold/90 font-body font-bold px-10 rounded-md">
              JOIN AS A SUPPORTER — $10/month
            </Button>
          </Link>
          <p className="font-signature text-white/80 text-2xl mt-6">Every Dollar Fights for Florida. — F.C. Clark</p>
        </div>
      </section>
    </div>
  );
}
